import { getDbSafe } from '../services/firebaseConfig';
import { doc, runTransaction, serverTimestamp, collection, deleteField, arrayUnion, arrayRemove, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { Forum, Tribe } from '../types';
import { applyGoldSpend } from './goldCore';

export const GovernanceService = {
    /**
     * Create Forum
     * Enforce limit: max 3 owned
     * Cost: 1 credit OR 500 Gold
     */
    async createForum(userId: string, forumData: Partial<Forum>) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const newForumRef = doc(collection(db, 'forums')); // Generate ID

        await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            if (!userDoc.exists()) throw new Error("User not found");
            const userData = userDoc.data();

            // 1. Enforce Caps
            const ownedForums = userData.ownedForumIds || [];
            const joinedForums = userData.joinedForumIds || [];
            const isLegion = userData.rank === 'legion';

            if (!isLegion && joinedForums.length >= 5) {
                throw new Error("You have reached the limit of 5 total Forums. Leave one to create another.");
            }
            if (!isLegion && ownedForums.length >= 5) {
                throw new Error("You have reached the limit of 5 owned Forums.");
            }

            // 2. Determine Cost (Credit vs Gold)
            let usedCredit = false;
            const credits = userData.forumCreationCredits || 0;
            if (credits > 0) {
                transaction.update(userRef, { forumCreationCredits: credits - 1 });
                usedCredit = true;
            } else {
                const COST = 500;
                // Charged inside THIS transaction, not via deductGold, so the
                // creation below and the payment succeed or fail together.
                applyGoldSpend(
                    transaction, userRef, userData, COST,
                    `You need ${COST} Gold to create a forum`,
                );
            }

            // 3. Create Forum Doc
            const now = serverTimestamp();
            const tags = forumData.tags || [];

            transaction.set(newForumRef, {
                ...forumData,
                id: newForumRef.id,
                ownerId: userId,
                isPublic: true, // ✅ Public by default
                status: 'Active',
                createdAt: now,
                lastActivityAt: now,
                lastOwnershipTransferAt: now,
                members: [userId], // Owner is first member
                memberCount: 1,
                moderators: [userId], // Owner is moderator
                postCount: 0,
                commentCount: 0,
                moderatorActionCount: 0,
                // Keyword frequency tracking
                keywordCounts: {}, // ✅ Initialize empty for yap-word counting
                // Discovery Initializers
                tags: tags,
                primaryTag: tags.length > 0 ? tags[0] : null,
                trendingScore: 0,
                posts7d: 0,
                comments7d: 0
            });

            // 4. Update User Owned & Joined Lists

            transaction.update(userRef, {
                ownedForumIds: [...ownedForums, newForumRef.id],
                joinedForumIds: [...new Set([...joinedForums, newForumRef.id])]
            });
        });

        // 5. Send notification to user about successful forum creation
        try {
            const { NotificationService } = await import('./api');
            await NotificationService.sendNotification(
                userId,
                'FORUM_CREATED',
                'Forum Established!',
                `Your forum "${forumData.name ?? 'New Forum'}" has been successfully established.`,
                { forumId: newForumRef.id, forumName: forumData.name ?? 'New Forum' }
            );
        } catch (e) {
            console.error('Failed to send forum creation notification:', e);
        }

        return newForumRef.id;
    },

    /**
     * Create Tribe
     * Enforce limit: max 2 owned
     * Cost: 1 credit OR 1000 Gold
     */
    async createTribe(userId: string, tribeData: Partial<Tribe>, paymentMethod?: 'credit' | 'gold') {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const newTribeRef = doc(collection(db, 'tribes'));

        await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            if (!userDoc.exists()) throw new Error("User not found");
            const userData = userDoc.data();

            // 1. Enforce Caps
            const ownedTribes = userData.ownedTribeIds || [];
            const joinedTribes = userData.joinedTribeIds || [];

            const isLegion = userData.rank === 'legion';

            if (!isLegion && joinedTribes.length >= 4) {
                throw new Error("You have reached the limit of 4 total Tribes. Leave one to establish another.");
            }
            if (!isLegion && ownedTribes.length >= 4) {
                throw new Error("You have reached the limit of 4 owned Tribes.");
            }

            // 2. Determine Cost based on method.
            // University tribes are a Legion-only administrative action (gated in
            // the creation modal) and carry no cost — the modal advertises none and
            // a Legion shouldn't be blocked/charged for establishing an official
            // university community. Skip the cost block entirely for them.
            const isUniversityTribe = tribeData.tribeType === 'university';
            const credits = userData.tribeCreationCredits || 0;
            const GOLD_COST = 1000;

            if (isUniversityTribe) {
                // no charge
            } else if (paymentMethod === 'gold') {
                applyGoldSpend(
                    transaction, userRef, userData, GOLD_COST,
                    `Insufficient gold. Need ${GOLD_COST} gold.`,
                );
            } else if (paymentMethod === 'credit') {
                if (credits <= 0) {
                    throw new Error("No tribe creation credits available.");
                }
                transaction.update(userRef, { tribeCreationCredits: credits - 1 });
            } else {
                // Default behavior: Prefer credits, then gold
                if (credits > 0) {
                    transaction.update(userRef, { tribeCreationCredits: credits - 1 });
                } else {
                    applyGoldSpend(
                        transaction, userRef, userData, GOLD_COST,
                        `Insufficient gold. Need ${GOLD_COST} gold or 1 creation credit.`,
                    );
                }
            }

            // 3. Create Tribe Doc
            const now = serverTimestamp();
            const tags = tribeData.tags || [];

            transaction.set(newTribeRef, {
                ...tribeData,
                id: newTribeRef.id,
                ownerId: userId,
                status: 'Active',
                createdAt: now,
                lastActivityAt: now,
                lastOwnershipTransferAt: now,
                members: [userId], // ✅ Correctly initialize as array
                memberCount: 1,
                moderators: [userId],
                postCount: 0,
                commentCount: 0,
                moderatorActionCount: 0,
                // Discovery Initializers
                tags: tags,
                primaryTag: tags.length > 0 ? tags[0] : null,
                trendingScore: 0,
                posts7d: 0,
                comments7d: 0,
                // University Tribes System — defaults ensure backward compatibility
                // tribeType/requiresApproval/hiddenOwner/schoolName/inviteCode may be
                // passed in via tribeData for university tribes; standard tribes get safe defaults.
                tribeType: tribeData.tribeType || 'standard',
                requiresApproval: tribeData.requiresApproval ?? false,
                hiddenOwner: tribeData.hiddenOwner ?? false,
                pendingMemberCount: 0,
            });

            // 4. Update User Owned & Joined Lists

            transaction.update(userRef, {
                ownedTribeIds: [...ownedTribes, newTribeRef.id],
                joinedTribeIds: [...new Set([...joinedTribes, newTribeRef.id])],
                // Legacy support: also add to `tribes` array string?
                tribes: [...(userData.tribes || []), tribeData.name || 'New Tribe']
            });
        });

        // 5. Send notification to user about successful tribe creation
        try {
            const { NotificationService } = await import('./api');
            await NotificationService.sendNotification(
                userId,
                'TRIBE_CREATED',
                'Tribe Established!',
                `Your tribe "${tribeData.name || 'New Tribe'}" has been successfully established.`,
                { tribeId: newTribeRef.id, tribeName: tribeData.name || 'New Tribe' }
            );
        } catch (e) {
            console.error('Failed to send tribe creation notification:', e);
        }

        return newTribeRef.id;
    },

    /**
     * Pass Ownership
     * 1. Check cooldown (24h).
     * 2. Swap owners in entity doc.
     * 3. Swap entity ID in user `ownedXXXIds` arrays.
     * 4. Create notification.
     */
    async passOwnership(entityType: 'forum' | 'tribe', entityId: string, currentOwnerId: string, newOwnerId: string) {
        if (currentOwnerId === newOwnerId) throw new Error("Cannot pass ownership to yourself.");

        const db = await getDbSafe();
        const entityCollection = entityType === 'forum' ? 'forums' : 'tribes';
        const entityRef = doc(db, entityCollection, entityId);
        const currentOwnerRef = doc(db, 'users', currentOwnerId);
        const newOwnerRef = doc(db, 'users', newOwnerId);
        const notificationRef = doc(collection(db, 'notifications'));

        await runTransaction(db, async (transaction) => {
            const entityDoc = await transaction.get(entityRef);
            if (!entityDoc.exists()) throw new Error("Entity not found");
            const entityData = entityDoc.data();

            if (entityData.ownerId !== currentOwnerId) {
                throw new Error("You are not the owner of this entity.");
            }

            // Cooldown bypass: university tribes whose hiddenOwner flag is still true
            // have never had a public owner — the first transfer is exempt from the 24h
            // cooldown so Legion can pass it to the founding student immediately after creation.
            const isFirstUniversityTransfer = entityType === 'tribe' && entityData.hiddenOwner === true;

            if (!isFirstUniversityTransfer) {
                const lastTransfer = entityData.lastOwnershipTransferAt ? entityData.lastOwnershipTransferAt.toDate() : null;
                if (lastTransfer) {
                    const hoursSince = (new Date().getTime() - lastTransfer.getTime()) / (1000 * 60 * 60);
                    if (hoursSince < 24) {
                        throw new Error("Ownership cannot be transferred more than once every 24 hours.");
                    }
                }
            }

            const currentOwnerDoc = await transaction.get(currentOwnerRef);
            const newOwnerDoc = await transaction.get(newOwnerRef);

            if (!newOwnerDoc.exists()) throw new Error("New owner user not found.");

            // 🔒 Ownership can only be passed to an EXISTING member. Check the
            // canonical member subcollection doc, falling back to the members
            // array on the entity doc (covers any array/subcollection drift).
            const newOwnerMemberRef = doc(db, entityCollection, entityId, 'members', newOwnerId);
            const newOwnerMemberDoc = await transaction.get(newOwnerMemberRef);
            const membersArr = Array.isArray(entityData.members) ? entityData.members.map(String) : [];
            const isMember = newOwnerMemberDoc.exists() || membersArr.includes(String(newOwnerId));
            if (!isMember) {
                throw new Error(`You can only pass ownership to a member of this ${entityType}.`);
            }

            // Update Entity
            transaction.update(entityRef, {
                ownerId: newOwnerId,
                lastOwnershipTransferAt: serverTimestamp(),
                moderators: entityData.moderators && !entityData.moderators.includes(newOwnerId)
                    ? [...entityData.moderators, newOwnerId]
                    : entityData.moderators,
                // 🔒 Ensure new owner is in members array
                members: arrayUnion(newOwnerId),
                // First transfer of a university tribe makes the new owner publicly visible.
                // All subsequent transfers leave hiddenOwner as false (already cleared).
                ...(isFirstUniversityTransfer && { hiddenOwner: false }),
            });

            // Update Users
            // Remove from old
            const ownedKey = entityType === 'forum' ? 'ownedForumIds' : 'ownedTribeIds';

            const oldList = currentOwnerDoc.data()?.[ownedKey] || [];
            transaction.update(currentOwnerRef, {
                [ownedKey]: oldList.filter((id: string) => id !== entityId)
            });

            // Add to new
            const newList = newOwnerDoc.data()?.[ownedKey] || [];
            // Enforce cap check for RECEIVER?
            // "Owner may own at most... Caps must be enforced atomically"
            // Yes, receiver must have space.
            const cap = (entityType === 'forum') ? 3 : 2;
            if (newList.length >= cap) {
                throw new Error(`Recipient already owns max number of ${entityType}s.`);
            }

            transaction.update(newOwnerRef, {
                [ownedKey]: [...newList, entityId]
            });

            // Create Notification
            transaction.set(notificationRef, {
                type: 'OWNERSHIP_TRANSFER',
                targetType: entityType,
                targetId: entityId,
                message: `Ownership passed from ${currentOwnerDoc.data()?.username?.startsWith('@') ? currentOwnerDoc.data()?.username : `@${currentOwnerDoc.data()?.username}`} to ${newOwnerDoc.data()?.username?.startsWith('@') ? newOwnerDoc.data()?.username : `@${newOwnerDoc.data()?.username}`}`,
                createdAt: serverTimestamp(),
                expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24h from now
                audience: 'community'
            });
        });
    },

    /**
     * Join Forum
     */
    async joinForum(userId: string, forumId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const forumRef = doc(db, 'forums', forumId);
        const membershipRef = doc(db, 'forums', forumId, 'members', userId);

        return await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            const forumDoc = await transaction.get(forumRef);
            const memberDoc = await transaction.get(membershipRef);

            if (!userDoc.exists()) throw new Error("User not found");
            if (!forumDoc.exists()) throw new Error("Forum not found");
            if (memberDoc.exists()) return; // Already a member

            const userData = userDoc.data();
            const joinedForums = userData.joinedForumIds || [];
            
            // No join limit — users can join unlimited forums

            const now = new Date().toISOString();

            // 1. Update Forum (Count + Array)
            const forumData = forumDoc.data();
            const updates: any = {
                memberCount: (forumData.memberCount || 0) + 1,
                trendingScore: (forumData.trendingScore || 0) + 5, // Boost on join
                lastActivityAt: serverTimestamp()
            };

            // 🔒 RESILIENCE: Only arrayUnion if current members is indeed an array or missing
            if (!forumData.members || Array.isArray(forumData.members)) {
                updates.members = arrayUnion(userId);
            } else {
                console.warn(`[GovernanceService] forum ${forumId} has legacy members field (type: ${typeof forumData.members}). Initializing as array.`);
                updates.members = [userId];
            }

            transaction.update(forumRef, updates);

            // 2. Add Membership Record
            transaction.set(membershipRef, {
                userId,
                joinedAt: serverTimestamp(),
                role: 'member'
            });

            // 3. Update User (Atomic Join + Timestamp)
            transaction.update(userRef, {
                joinedForumIds: [...new Set([...joinedForums, forumId])],
                [`forumJoinTimestamps.${forumId}`]: now
            });

            return { ownerId: forumData.ownerId, forumName: forumData.name };
        });
    },

    /**
     * Leave Forum
     */
    async leaveForum(userId: string, forumId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const forumRef = doc(db, 'forums', forumId);
        const membershipRef = doc(db, 'forums', forumId, 'members', userId);

        await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            const forumDoc = await transaction.get(forumRef);
            const memberDoc = await transaction.get(membershipRef);

            if (!userDoc.exists() || !forumDoc.exists() || !memberDoc.exists()) return;

            // Cannot leave if owner
            if (forumDoc.data().ownerId === userId) {
                throw new Error("Owners cannot leave their own forum. Pass ownership first.");
            }

            const userData = userDoc.data();
            const joinedForums = userData.joinedForumIds || [];

            // 1. Update Forum (Count + Array)
            const forumData = forumDoc.data();
            const updates: any = {
                memberCount: Math.max(0, (forumData.memberCount || 1) - 1),
                lastActivityAt: serverTimestamp()
            };

            if (Array.isArray(forumData.members)) {
                updates.members = arrayRemove(userId);
            }

            transaction.update(forumRef, updates);

            // 2. Remove Membership Record
            transaction.delete(membershipRef);

            // 3. Update User (Removal + Clear Timestamp)
            transaction.update(userRef, {
                joinedForumIds: joinedForums.filter((id: string) => id !== forumId),
                [`forumJoinTimestamps.${forumId}`]: deleteField()
            });
        });
    },

    /**
     * Join Tribe
     */
    async joinTribe(userId: string, tribeId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const tribeRef = doc(db, 'tribes', tribeId);
        const membershipRef = doc(db, 'tribes', tribeId, 'members', userId);

        return await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            const tribeDoc = await transaction.get(tribeRef);
            const memberDoc = await transaction.get(membershipRef);

            if (!userDoc.exists()) throw new Error("User not found");
            if (!tribeDoc.exists()) throw new Error("Tribe not found");
            if (memberDoc.exists()) return; // Already a member

            const userData = userDoc.data();
            const joinedTribes = userData.joinedTribeIds || [];
            
            // No join limit — users can join unlimited tribes

            const now = new Date().toISOString();

            // 1. Update Tribe
            const tribeData = tribeDoc.data();
            const updates: any = {
                memberCount: (tribeData.memberCount || 0) + 1,
                trendingScore: (tribeData.trendingScore || 0) + 5, // Boost on join
                lastActivityAt: serverTimestamp()
            };

            // 🔒 RESILIENCE: Only arrayUnion if current members is indeed an array or missing
            if (!tribeData.members || Array.isArray(tribeData.members)) {
                updates.members = arrayUnion(userId);
            } else {
                console.warn(`[GovernanceService] tribe ${tribeId} has legacy members field (type: ${typeof tribeData.members}). Initializing as array.`);
                updates.members = [userId];
            }

            transaction.update(tribeRef, updates);

            // 2. Add Membership Record
            transaction.set(membershipRef, {
                userId,
                joinedAt: serverTimestamp(),
                role: 'member'
            });

            // 3. Update User
            transaction.update(userRef, {
                joinedTribeIds: [...new Set([...joinedTribes, tribeId])],
                [`tribeJoinTimestamps.${tribeId}`]: now
            });

            return { ownerId: tribeData.ownerId, tribeName: tribeData.name };
        });
    },

    /**
     * Leave Tribe
     */
    async leaveTribe(userId: string, tribeId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const tribeRef = doc(db, 'tribes', tribeId);
        const membershipRef = doc(db, 'tribes', tribeId, 'members', userId);

        await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            const tribeDoc = await transaction.get(tribeRef);
            const memberDoc = await transaction.get(membershipRef);

            if (!userDoc.exists() || !tribeDoc.exists() || !memberDoc.exists()) return;

            if (tribeDoc.data().ownerId === userId) {
                throw new Error("Owners cannot leave their own tribe. Pass ownership first.");
            }

            const userData = userDoc.data();
            const joinedTribes = userData.joinedTribeIds || [];

            // 1. Update Tribe
            const tribeData = tribeDoc.data();
            const updates: any = {
                memberCount: Math.max(0, (tribeData.memberCount || 1) - 1),
                lastActivityAt: serverTimestamp()
            };

            if (Array.isArray(tribeData.members)) {
                updates.members = arrayRemove(userId);
            }

            transaction.update(tribeRef, updates);

            // 2. Remove Membership Record
            transaction.delete(membershipRef);

            // 3. Update User
            transaction.update(userRef, {
                joinedTribeIds: joinedTribes.filter((id: string) => id !== tribeId),
                [`tribeJoinTimestamps.${tribeId}`]: deleteField()
            });
        });
    },

    /**
     * Get Group Members
     * Fetches details of members from the subcollection
     */
    async getGroupMembers(entityType: 'forum' | 'tribe', entityId: string) {
        const db = await getDbSafe();
        const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');
        const memberCollection = collection(db, entityType === 'forum' ? 'forums' : 'tribes', entityId, 'members');
        const snapshot = await getDocs(memberCollection);
        
        const members: any[] = snapshot.docs.map((d) => ({
            id: d.id,
            ...d.data()
        }));

        // Fetch parent entity to explicitly include the owner
        const entityRef = doc(db, entityType === 'forum' ? 'forums' : 'tribes', entityId);
        const entitySnap = await getDoc(entityRef);
        if (entitySnap.exists()) {
            const ownerId = entitySnap.data().ownerId;
            if (ownerId) {
                const ownerIndex = members.findIndex(m => (m.userId || m.id) === ownerId);
                if (ownerIndex === -1) {
                    members.push({ id: ownerId, userId: ownerId, role: 'owner' });
                } else {
                    members[ownerIndex].role = 'owner';
                }
            }
        }

        return members;
    },

    /**
     * Update Group Settings
     */
    async updateGroup(entityType: 'forum' | 'tribe', entityId: string, updates: any) {
        const db = await getDbSafe();
        const { updateDoc } = await import('firebase/firestore');
        const entityRef = doc(db, entityType === 'forum' ? 'forums' : 'tribes', entityId);
        await updateDoc(entityRef, {
            ...updates,
            updatedAt: serverTimestamp()
        });
    },

    /**
     * Update Member Role
     */
    async updateMemberRole(entityType: 'forum' | 'tribe', entityId: string, userId: string, newRole: string) {
        const db = await getDbSafe();
        const { updateDoc } = await import('firebase/firestore');
        const membershipRef = doc(db, entityType === 'forum' ? 'forums' : 'tribes', entityId, 'members', userId);
        
        await updateDoc(membershipRef, {
            role: newRole,
            updatedAt: serverTimestamp()
        });

        // Also update moderators list in the main doc if applicable
        const entityRef = doc(db, entityType === 'forum' ? 'forums' : 'tribes', entityId);
        await runTransaction(db, async (transaction) => {
            const entityDoc = await transaction.get(entityRef);
            if (!entityDoc.exists()) return;
            const data = entityDoc.data();
            let moderators = data.moderators || [];
            
            if (newRole === 'moderator' || newRole === 'admin' || newRole === 'owner') {
                if (!moderators.includes(userId)) {
                    moderators.push(userId);
                }
            } else {
                moderators = moderators.filter((m: string) => m !== userId);
            }

            transaction.update(entityRef, { moderators });
        });
    },

    /**
     * Delete Forum
     * Owners or Legion only.
     */
    async deleteForum(userId: string, forumId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const forumRef = doc(db, 'forums', forumId);

        await runTransaction(db, async (transaction) => {
            // 1. READS
            const userDoc = await transaction.get(userRef);
            const forumDoc = await transaction.get(forumRef);

            if (!forumDoc.exists()) throw new Error("Forum not found");
            
            const forumData = forumDoc.data();
            const userData = userDoc.exists() ? userDoc.data() : {};
            
            const actualOwnerId = forumData.ownerId;
            const isOwner = actualOwnerId === userId;
            const isAdmin = userData.rank === 'legion';

            if (!isOwner && !isAdmin) {
                throw new Error("Only the owner or an admin can delete this forum.");
            }

            // If owner is different, we need their doc too to update their lists
            let ownerDoc = null;
            const ownerRef = actualOwnerId ? doc(db, 'users', actualOwnerId) : null;
            
            if (ownerRef && actualOwnerId !== userId) {
                 ownerDoc = await transaction.get(ownerRef);
            } else if (isOwner) {
                 ownerDoc = userDoc; // Same doc
            }

            // 2. WRITES
            transaction.delete(forumRef);

            // Remove from deleter's lists (if they have it)
            if (userDoc.exists()) {
                 const joinedForums = userData.joinedForumIds || [];
                 const ownedForums = userData.ownedForumIds || [];
                 
                 // If not owner (admin), we only remove from joined. If owner, we remove from owned too (handled below or here)
                 // Simpler: Just filter both for the current user regardless
                 transaction.update(userRef, {
                    ownedForumIds: ownedForums.filter((id: string) => id !== forumId),
                    joinedForumIds: joinedForums.filter((id: string) => id !== forumId)
                 });
            }

            // If actual owner is different (Admin deleted it), update actual owner's lists
            if (ownerDoc && actualOwnerId !== userId && ownerDoc.exists()) {
                 const ownerData = ownerDoc.data();
                 const ownedForums = ownerData.ownedForumIds || [];
                 const joinedForums = ownerData.joinedForumIds || [];
                 transaction.update(ownerRef!, {
                    ownedForumIds: ownedForums.filter((id: string) => id !== forumId),
                    joinedForumIds: joinedForums.filter((id: string) => id !== forumId)
                 });
            }
        });
    },

    /**
     * Delete Tribe
     * Owners or Legion only.
     */
    async deleteTribe(userId: string, tribeId: string) {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', userId);
        const tribeRef = doc(db, 'tribes', tribeId);

        await runTransaction(db, async (transaction) => {
            // 1. READS
            const userDoc = await transaction.get(userRef);
            const tribeDoc = await transaction.get(tribeRef);

            if (!tribeDoc.exists()) throw new Error("Tribe not found");

            const tribeData = tribeDoc.data();
            const userData = userDoc.exists() ? userDoc.data() : {};

            const actualOwnerId = tribeData.ownerId;
            const isOwner = actualOwnerId === userId;
            const isAdmin = userData.rank === 'legion';

            if (!isOwner && !isAdmin) {
                throw new Error("Only the owner or an admin can delete this tribe.");
            }

             // If owner is different, we need their doc too
            let ownerDoc = null;
            const ownerRef = actualOwnerId ? doc(db, 'users', actualOwnerId) : null;
            
            if (ownerRef && actualOwnerId !== userId) {
                 ownerDoc = await transaction.get(ownerRef);
            } else if (isOwner) {
                 ownerDoc = userDoc;
            }

            // 2. WRITES
            transaction.delete(tribeRef);

            // Remove from deleter's lists
            if (userDoc.exists()) {
                 const joinedTribes = userData.joinedTribeIds || [];
                 const ownedTribes = userData.ownedTribeIds || [];
                 
                 transaction.update(userRef, {
                    ownedTribeIds: ownedTribes.filter((id: string) => id !== tribeId),
                    joinedTribeIds: joinedTribes.filter((id: string) => id !== tribeId)
                 });
            }

            // If actual owner is different, update their lists
            if (ownerDoc && actualOwnerId !== userId && ownerDoc.exists()) {
                 const ownerData = ownerDoc.data();
                 const ownedTribes = ownerData.ownedTribeIds || [];
                 const joinedTribes = ownerData.joinedTribeIds || [];
                 transaction.update(ownerRef!, {
                    ownedTribeIds: ownedTribes.filter((id: string) => id !== tribeId),
                    joinedTribeIds: joinedTribes.filter((id: string) => id !== tribeId)
                 });
            }
        });
    },
    /**
     * Accept Group Invite
     * Called when a user taps "Accept" on a DM group invite.
     * Bypasses the isPublic check — the invite IS the authorization.
     * Grants full 'member' role, same rights as any joined member.
     */
    // ─── University Tribe Join Request Flow (Phase 3) ───────────────────────────


    /**
     * getJoinRequests
     * Fetches all pending join requests for a tribe (owner view).
     */
    async getJoinRequests(tribeId: string): Promise<any[]> {
        const db = await getDbSafe();
        const { collection, getDocs, query, where, orderBy } = await import('firebase/firestore');
        const q = query(
            collection(db, 'tribes', tribeId, 'join_requests'),
            where('status', '==', 'pending'),
            orderBy('requestedAt', 'asc')
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    /**
     * getMyJoinRequest
     * Returns the current user's own join request for a tribe, or null.
     */
    async getMyJoinRequest(userId: string, tribeId: string): Promise<any | null> {
        const db = await getDbSafe();
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'tribes', tribeId, 'join_requests', userId));
        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    },

    // ────────────────────────────────────────────────────────────────────────────

    async acceptGroupInvite(entityType: 'forum' | 'tribe', entityId: string, userId: string) {
        const db = await getDbSafe();
        const entityCollection = entityType === 'forum' ? 'forums' : 'tribes';
        const userRef = doc(db, 'users', userId);
        const entityRef = doc(db, entityCollection, entityId);
        const membershipRef = doc(db, entityCollection, entityId, 'members', userId);

        return await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            const entityDoc = await transaction.get(entityRef);
            const memberDoc = await transaction.get(membershipRef);

            if (!userDoc.exists()) throw new Error('User not found');
            if (!entityDoc.exists()) throw new Error(`${entityType} not found`);
            if (memberDoc.exists()) return; // Already a member — no-op

            const userData = userDoc.data();
            const entityData = entityDoc.data();

            // Enforce total join cap even for invite acceptances
            const joinedKey = entityType === 'forum' ? 'joinedForumIds' : 'joinedTribeIds';
            const joinedList = userData[joinedKey] || [];
            const cap = entityType === 'forum' ? 5 : 4;
            if (joinedList.length >= cap) {
                throw new Error(`You have reached the limit of ${cap} total ${entityType}s. Leave one first.`);
            }

            // Update entity member count
            const countUpdates: any = {
                memberCount: (entityData.memberCount || 0) + 1,
                lastActivityAt: serverTimestamp()
            };
            if (!entityData.members || Array.isArray(entityData.members)) {
                countUpdates.members = arrayUnion(userId);
            } else {
                countUpdates.members = [userId];
            }
            transaction.update(entityRef, countUpdates);

            // Write membership record — full member via invite
            transaction.set(membershipRef, {
                userId,
                joinedAt: serverTimestamp(),
                role: 'member',
                inviteAccepted: true
            });

            // Update user's joined list
            const now = new Date().toISOString();
            const timestampKey = entityType === 'forum' ? 'forumJoinTimestamps' : 'tribeJoinTimestamps';
            transaction.update(userRef, {
                [joinedKey]: [...new Set([...joinedList, entityId])],
                [`${timestampKey}.${entityId}`]: now
            });

            return { entityName: entityData.name, ownerId: entityData.ownerId };
        });
    },

    /**
     * Create a join request for an approval-gated tribe.
     * Writes to tribes/{tribeId}/join_requests/{userId}, increments pendingMemberCount,
     * and notifies the tribe owner via the existing notification pipeline.
     */
    async createJoinRequest(userId: string, tribeId: string, fromInviteCode: string | null, schoolEmail: string) {
        const db = await getDbSafe();
        const tribeRef = doc(db, 'tribes', tribeId);
        const memberRef = doc(db, 'tribes', tribeId, 'members', userId);
        const requestRef = doc(db, 'tribes', tribeId, 'join_requests', userId);

        const [tribeSnap, memberSnap, existingReqSnap] = await Promise.all([
            getDoc(tribeRef),
            getDoc(memberRef),
            getDoc(requestRef),
        ]);

        if (!tribeSnap.exists()) throw new Error('Tribe not found.');
        const tribeData = tribeSnap.data();
        if (!tribeData.requiresApproval) throw new Error('This tribe does not require approval.');
        if (memberSnap.exists()) throw new Error('You are already a member of this tribe.');

        // Option B for denied re-requests: only block 'pending' — 'denied' falls through
        // and the setDoc below overwrites the old document with a fresh pending request.
        if (existingReqSnap.exists()) {
            const existingStatus = existingReqSnap.data().status;
            if (existingStatus === 'pending') {
                throw new Error('You already have a pending join request for this tribe.');
            }
            if (existingStatus === 'approved') {
                throw new Error('Your previous request was approved. Please reload the app.');
            }
            // 'denied' → fall through and overwrite with a fresh pending request
        }

        // Fetch requester name for notification body
        let requesterName = 'Someone';
        try {
            const userSnap = await getDoc(doc(db, 'users', userId));
            if (userSnap.exists()) {
                const ud = userSnap.data();
                requesterName = ud.name || ud.username || 'Someone';
            }
        } catch {}

        await setDoc(requestRef, {
            userId,
            requestedAt: serverTimestamp(),
            status: 'pending',
            fromInviteCode: fromInviteCode ?? null,
            message: null,
            schoolEmail,
        });

        await updateDoc(tribeRef, { pendingMemberCount: increment(1) });

        // Skip the self-notify. The Cloud Function that used to send a second,
        // duplicate push for this event carried this guard; now that the
        // notification doc is the only sender, it has to live here.
        if (tribeData.ownerId && String(tribeData.ownerId) !== String(userId)) {
            try {
                const { NotificationService } = await import('./api');
                await NotificationService.sendNotification(
                    tribeData.ownerId,
                    'TRIBE_JOIN_REQUEST',
                    'New join request',
                    `${requesterName} wants to join ${tribeData.name || 'your tribe'}`,
                    {
                        tribeId,
                        tribeName: tribeData.name || '',
                        requestUserId: userId,
                        fromUserId: userId,
                        fromUserName: requesterName,
                    }
                );
            } catch (e) {
                console.error('[GovernanceService] TRIBE_JOIN_REQUEST notification failed:', e);
            }
        }
    },

    /**
     * Approve a join request.
     * Updates the request status to 'approved', decrements pendingMemberCount,
     * and notifies the requester. The Cloud Function onJoinRequestApproved handles
     * the actual membership write via Admin SDK.
     */
    async approveJoinRequest(ownerId: string, tribeId: string, requestUserId: string) {
        const db = await getDbSafe();
        const tribeRef = doc(db, 'tribes', tribeId);
        const requestRef = doc(db, 'tribes', tribeId, 'join_requests', requestUserId);

        const tribeSnap = await getDoc(tribeRef);
        if (!tribeSnap.exists()) throw new Error('Tribe not found.');
        const tribeData = tribeSnap.data();
        if (String(tribeData.ownerId) !== String(ownerId)) throw new Error('Only the tribe owner can approve requests.');

        await updateDoc(requestRef, { status: 'approved' });
        await updateDoc(tribeRef, { pendingMemberCount: increment(-1) });

        try {
            const { NotificationService } = await import('./api');
            await NotificationService.sendNotification(
                requestUserId,
                'TRIBE_JOIN_APPROVED',
                'Request approved!',
                `Your request to join ${tribeData.name || 'the tribe'} was approved 🎉`,
                { tribeId, tribeName: tribeData.name || '' }
            );
        } catch (e) {
            console.error('[GovernanceService] TRIBE_JOIN_APPROVED notification failed:', e);
        }
    },

    /**
     * Deny a join request.
     * Updates request status to 'denied', decrements pendingMemberCount,
     * and notifies the requester.
     */
    async denyJoinRequest(ownerId: string, tribeId: string, requestUserId: string) {
        const db = await getDbSafe();
        const tribeRef = doc(db, 'tribes', tribeId);
        const requestRef = doc(db, 'tribes', tribeId, 'join_requests', requestUserId);

        const tribeSnap = await getDoc(tribeRef);
        if (!tribeSnap.exists()) throw new Error('Tribe not found.');
        const tribeData = tribeSnap.data();
        if (String(tribeData.ownerId) !== String(ownerId)) throw new Error('Only the tribe owner can deny requests.');

        await updateDoc(requestRef, { status: 'denied' });
        await updateDoc(tribeRef, { pendingMemberCount: increment(-1) });

        try {
            const { NotificationService } = await import('./api');
            await NotificationService.sendNotification(
                requestUserId,
                'TRIBE_JOIN_DENIED',
                'Request not approved',
                `Your request to join ${tribeData.name || 'the tribe'} was not approved`,
                { tribeId, tribeName: tribeData.name || '' }
            );
        } catch (e) {
            console.error('[GovernanceService] TRIBE_JOIN_DENIED notification failed:', e);
        }
    },
};
